import { createApp } from "vue";
import Antd from "ant-design-vue";
import axios from "axios";
import App from "./App.vue";
import router from "@/router";
import store from "@/store";
import { DO_LOGOUT } from "@/store/modules/autenticacao/types";
import { install } from "vue3-recaptcha-v2";
import VueTheMask from "vue-the-mask";
import NewMessage from "@/utils/newMessage.js";

import "@/assets/styles/theme.less";

/* ================================================================
   Carregamento dos JSON de configuração
   - instância própria do axios (sem os interceptors globais)
   - sem cache (query string com timestamp + headers no-cache)
   - valida se o retorno é um JSON de verdade (e não o index.html)
   - tenta de novo até 3 vezes antes de mostrar o erro
   ================================================================ */
const CONFIG_BASE_URL = "/config/";
const MAX_TENTATIVAS = 3;

const configHttp = axios.create({
	timeout: 15000,
	headers: {
		"Cache-Control": "no-cache, no-store, must-revalidate",
		Pragma: "no-cache",
		Expires: "0"
	}
});

function aguardar(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

async function buscarJson(arquivo) {
	let ultimoErro = null;

	for (let tentativa = 1; tentativa <= MAX_TENTATIVAS; tentativa++) {
		try {
			const response = await configHttp.get(CONFIG_BASE_URL + arquivo, {
				params: { v: Date.now() } // cache-busting: URL diferente a cada acesso
			});

			let dados = response.data;

			// O servidor pode devolver texto em vez de objeto (mime errado ou BOM)
			if (typeof dados === "string") {
				const texto = dados.replace(/^\uFEFF/, "").trim();

				// Se veio HTML, a reescrita do SPA devolveu o index.html
				if (texto.charAt(0) === "<") {
					throw new Error(`O servidor retornou HTML em vez de JSON para ${arquivo}`);
				}

				dados = JSON.parse(texto);
			}

			if (!dados || typeof dados !== "object") {
				throw new Error(`Conteúdo inválido em ${arquivo}`);
			}

			return dados;
		} catch (erro) {
			ultimoErro = erro;
			console.warn(
				`[config] Falha ao carregar ${arquivo} (tentativa ${tentativa}/${MAX_TENTATIVAS})`,
				erro
			);

			if (tentativa < MAX_TENTATIVAS) {
				await aguardar(500 * tentativa); // espera 500ms, depois 1000ms
			}
		}
	}

	throw ultimoErro;
}

async function carregarConfiguracoes(app) {
	const [login, novidade, iconesProdutos, modalCentral] = await Promise.all([		
		buscarJson("portalData.json"),		
		buscarJson("novidadeData.json"),
		buscarJson("iconesProdutos.json"),
		buscarJson("modalCentral.json")
	]);

	const config = Object.freeze({
		login,
		novidade,
		iconesProdutos,
		modalCentral
	});

	// Options API: this.$config
	app.config.globalProperties.$config = config;

	// Composition API: const config = inject("config")
	app.provide("config", config);
}

function removerLoading() {
	const loading = document.getElementById("app-loading");

	if (!loading) {
		return;
	}

	loading.classList.add("hide");

	setTimeout(() => {
		loading.remove();
	}, 400);
}

function exibirErroLoading() {
	const loading = document.getElementById("app-loading");

	if (!loading) {
		return;
	}

	loading.innerHTML = `
		<div class="app-loading-content">
			<div class="app-loading-logo">
				<img
					src="/kurierLogo2.png"
					alt="Kurier Portal Cliente"
					style="width: 350px;"
				/>
			</div>

			<div class="app-loading-title">
				Não foi possível iniciar o sistema
			</div>

			<div class="app-loading-description">
				Aguarde alguns minutos e tente novamente.<br/>
				Ou entre em contato com a Kurier.
			</div>
		</div>
	`;
}

async function iniciarAplicacao() {
	try {
		const app = createApp(App);

		app.config.globalProperties.$newMessage = NewMessage;

		app.config.globalProperties.$logout = () => {
			store.dispatch(DO_LOGOUT);
			router.push({ name: "login" });
		};

		await carregarConfiguracoes(app);

		app
			.use(install, {
				sitekey: process.env.VUE_APP_KEY_RECAPTCHA_V2,
				cnDomains: false
			})
			.use(Antd)
			.use(store)
			.use(router)
			.use(VueTheMask);

		app.mount("#app");

		removerLoading();
	} catch (error) {
		console.error("Erro ao iniciar aplicação:", error);
		exibirErroLoading();
	}
}

iniciarAplicacao();